/*
  assets/menu.js
  Drives the menu in sections/menu.liquid (the menu-blocks header), and moves it
  between the header and the drawer at the theme's 1024px breakpoint.

  Desktop: the menu renders in the header's own [data-menu-home] column and
  stays there. Below 1024px the theme hides that column and serves the menu from
  the hamburger drawer instead -- but that drawer is NOT rendered with the page.
  snippets/render_bottom.liquid only emits the real markup under `admin_sp`; on a
  storefront it emits skeleton placeholders, and assets/interactable.min.js then
  fetches T4Sroutes.search_url + '/?view=mn' as a SEPARATE DOCUMENT and does
  `$mobileNav.html(response)` -- replacing the drawer's entire contents. So
  #shopify-mb_nav does not exist at load, on any template, and anything sitting
  inside the drawer beforehand is destroyed by that swap.

  Hence the observer below: we seat into the drawer only once the swap has
  happened, re-seat if a later swap evicts us, and go back to [data-menu-home]
  when the viewport widens again. The observer closes over the element, so
  being detached does not collect it.
*/
if (!customElements.get('menu-nav')) {
  customElements.define(
    'menu-nav',
    class MenuNav extends HTMLElement {
      // The header column the menu lives in on desktop (sections/menu.liquid).
      static HOME = '[data-menu-home]';
      static DRAWER = '#shopify-mb_nav';
      // The element that scrolls when the drawer overflows; measured, it is
      // the drawer itself, not #shopify-mb_nav or this element.
      static SCROLLER = '#t4s-menu-drawer';

      connectedCallback() {
        // 1024 is the theme's own drawer breakpoint and must stay in step with
        // the media query in menu.css.
        if (!this.query) {
          this.query = window.matchMedia('(max-width: 1024px)');
          this.onClick = this.onClick.bind(this);
          this.onChange = this.onChange.bind(this);
          this.onTabPointer = this.onTabPointer.bind(this);
          this.onTabFocus = this.onTabFocus.bind(this);
          this.onTabKey = this.onTabKey.bind(this);
          this.onBlockSelect = this.onBlockSelect.bind(this);
          this.onBlockDeselect = this.onBlockDeselect.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('pointerover', this.onTabPointer);
          this.addEventListener('pointerout', this.onTabPointer);
          this.addEventListener('focusin', this.onTabFocus);
          this.addEventListener('keydown', this.onTabKey);
          this.query.addEventListener('change', this.onChange);
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
          this.measure = this.measure.bind(this);
          this.addEventListener('pointerover', this.measure, { passive: true });
          this.onFitOpen = this.onFitOpen.bind(this);
          this.addEventListener('pointerover', this.onFitOpen, { passive: true });
          this.addEventListener('focusin', this.onFitOpen);
          this.addEventListener('focusin', this.measure);
          window.addEventListener('resize', this.measure, { passive: true });
          window.addEventListener('load', this.measure);
          // The sticky header swaps logos, which can change the header's height
          // while a panel stays open or keeps focus (#447): re-measure whenever
          // the header or this element changes size.
          if ('ResizeObserver' in window) this.resizer = new ResizeObserver(this.measure);
        }
        if (!this.watcher) this.watch();
        // Relocating calls appendChild, which fires disconnectedCallback then
        // connectedCallback again. Without the guard that recurses forever, and
        // without the `if (!this.query)` above it would stack a listener per hop.
        this.place();
        this.measure();
        // A move (desktop <-> drawer) disconnected the pin's watcher. Back in
        // the desktop slot, pin the selected item again; in the drawer, close it.
        if (this.pinned) {
          const item = this.pinned;
          if (this.query.matches || !this.contains(item)) item.classList.remove(MenuNav.OPEN);
          else this.editorOpen(item);
        }
        if (this.resizer) {
          this.resizer.observe(this);
          const header = this.closest('.t4s-header__wrapper');
          if (header) this.resizer.observe(header);
        }
      }

      /*
        Where the full-width mega panels go (assets/menu.css, #324). A panel is
        positioned against this element, which sits part-way across the header,
        so "Full width" needs this element's left edge to start at the screen's
        left, "Content full width" needs the page content's box, a centred
        Custom width panel needs the screen's middle, and every panel needs the
        gap from this element's bottom to the header's (#447). The content
        box comes from an empty .t4s-container, so it follows the store's own
        wrapper-width setting and breakpoints rather than a number copied here.
        At most once a frame, desktop only, and only when a mega panel exists;
        hovering the menu re-measures in case the header has shifted
        (a late logo, a font swap) since the last resize.
      */
      measure() {
        // A theme-editor section reload leaves the replaced element's window
        // listeners behind; a detached copy has nothing to measure.
        if (!this.isConnected || this.measuring || this.query.matches) return;
        if (!this.querySelector('.t4s-type__mega')) return;
        this.measuring = requestAnimationFrame(() => {
          this.measuring = 0;
          const nav = this.getBoundingClientRect();
          const content = MenuNav.contentProbe().getBoundingClientRect();
          const header = (this.closest('.t4s-header__wrapper') || this.closest('.t4s-section-header') || this).getBoundingClientRect();
          this.style.setProperty('--menu-nav-x', `${nav.left}px`);
          this.style.setProperty('--menu-vw', `${document.documentElement.clientWidth}px`);
          this.style.setProperty('--menu-content-x', `${content.left}px`);
          this.style.setProperty('--menu-content-w', `${content.width}px`);
          this.style.setProperty('--menu-panel-gap', `${Math.max(0, Math.round(header.bottom - nav.bottom))}px`);
          this.setAttribute('data-menu-measured', '');
          // A resize or header change can change an open fit panel's width;
          // place it again so it doesn't keep a stale offset (#502 plan review).
          this.querySelectorAll(`.t4s-menu-item.menu-width__fit.${MenuNav.OPEN} > .t4s-sub-menu`).forEach((panel) => {
            this.placeFit(panel.parentElement, panel);
          });
        });
      }

      /*
        "Fit columns" panels (#502) open centred under the item that opened
        them, as close to it as the screen allows, then kept 15px inside the
        screen's edges (Lee, 2026-10-13: centred on the hovered item, not the
        screen). Clamping to the page content instead held wide panels at the
        content's left edge on wide screens, 500-700px from their item. The theme would centre a panel that
        fits on the *screen* (.is--center-screen), which put Just Pro Tools'
        Accessories panel 200px left of its item, and pushes one that doesn't
        fit to 5px from the screen edge (Floating UI shift), left of the logo.
        So for fit panels this places them itself, once the theme has: wait
        two frames after the pointer or focus arrives, then measure and set
        left. Event-driven on purpose: a MutationObserver on the panel's style
        answered its own writes and the theme's, and froze the Plus test store
        preview on hover (2026-10-12). One pending frame per panel, so a later
        pointerover replaces the earlier one and the panel is measured settled.
      */
      onFitOpen(event) {
        if (this.query.matches) return;
        const item = event.target.closest && event.target.closest('.t4s-menu-item.menu-width__fit');
        if (!item) return;
        const panel = item.querySelector(':scope > .t4s-sub-menu');
        if (!panel) return;
        if (panel.menuFitFrame) cancelAnimationFrame(panel.menuFitFrame);
        panel.menuFitFrame = requestAnimationFrame(() => {
          panel.menuFitFrame = requestAnimationFrame(() => {
            panel.menuFitFrame = 0;
            this.placeFit(item, panel);
          });
        });
      }

      placeFit(item, panel) {
        const link = item.querySelector(':scope > a') || item;
        const anchor = link.getBoundingClientRect();
        const box = panel.getBoundingClientRect();
        if (!box.width) return;
        // Centred under the item, then kept GUTTER px inside the screen (the
        // same 15px the CSS max-width leaves each side).
        const GUTTER = 15;
        const screen = document.documentElement.clientWidth;
        let target = anchor.left + anchor.width / 2 - box.width / 2;
        target = Math.min(target, screen - GUTTER - box.width);
        target = Math.max(target, GUTTER);
        const shift = target - box.left;
        if (Math.abs(shift) < 1) return;
        panel.style.left = `${panel.offsetLeft + shift}px`;
      }

      static contentProbe() {
        let probe = document.querySelector('[data-menu-content-probe]');
        if (!probe) {
          probe = document.createElement('div');
          probe.className = 't4s-container';
          probe.setAttribute('data-menu-content-probe', '');
          probe.setAttribute('aria-hidden', 'true');
          probe.style.cssText = 'height:0;padding-block:0;margin-block:0;overflow:hidden;visibility:hidden;';
          (document.getElementById('MainContent') || document.body).appendChild(probe);
        }
        return probe;
      }

      /*
        The drawer arrives asynchronously and can be replaced wholesale (see the
        file header), so a one-shot placement at connect is not enough. Watching
        the drawer container -- which IS present at load, as a skeleton -- covers
        both the first swap and any later one. Our own insertion mutates this
        subtree too, but place() returns early once we are already in the slot,
        so it settles after one pass rather than looping.
      */
      watch() {
        const drawer = document.querySelector('#t4s-menu-drawer');
        if (!drawer) return;
        this.watcher = new MutationObserver(() => this.place());
        this.watcher.observe(drawer, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        // Deliberately does NOT unbind: a move disconnects us, and we want the
        // same listeners on the other side of it. Teardown would need a real
        // removal, which this section never does. The size observer is the
        // exception: it would keep watching a header we've left or a section
        // the editor replaced, and connectedCallback observes again after a move.
        if (this.resizer) this.resizer.disconnect();
        if (this.pinWatcher) this.pinWatcher.disconnect();
      }

      place() {
        if (this.moving) return;
        if (!this.home) this.home = this.closest(MenuNav.HOME) || document.querySelector(MenuNav.HOME);
        const root = this.query.matches
          ? document.querySelector(MenuNav.DRAWER)
          : this.home;
        /*
          Below the breakpoint with no drawer yet, the menu is still sitting in its
          header column. That column is display:none below 1024px anyway, but a
          stack of open panels measured 579px tall when it was not. It has to stay out of sight until
          the drawer exists to receive it, and `parked` says exactly that rather
          than leaving a bare display:none nobody can account for.
        */
        if (!root) {
          if (this.query.matches) this.setAttribute('data-menu-nav-parked', '');
          return;
        }
        /*
          If the mount point is inside us, moving into it would make us our own
          ancestor and the DOM throws HierarchyRequestError. Seen for real: a
          re-swap of the drawer left a #shopify-mb_nav inside this element, the
          prepend threw, and because the throw landed between `moving = true` and
          `moving = false` the re-entrancy guard latched on forever -- every later
          place() returned at the first line and the menu never came back. Both
          halves of that are fixed here: this guard, and the try/finally below.
        */
        if (this.contains(root)) return;
        this.removeAttribute('data-menu-nav-parked');
        const slot = root;
        if (this.parentElement === slot) return;
        this.moving = true;
        try {
          // Drawer: above mb_nav's utility blocks (wishlist, account, currency),
          // which stay. Header: the column is otherwise ours.
          if (this.query.matches) slot.prepend(this);
          else slot.appendChild(this);
        } finally {
          this.moving = false;
        }
        this.displace(slot);
      }

      /*
        Hide whatever nav was already in the slot, rather than doing it from CSS.
        A stylesheet rule would fire on every store the moment the files sync,
        including the ones that have not migrated yet and still need their old
        menu -- menu-nav renders nothing at all there, so this code never runs and
        their nav is untouched. The hide is therefore scoped to stores that have
        actually authored items into the new section.
      */
      displace(slot) {
        if (this.query.matches) return this.displaceDrawer(slot);
        /*
          Descendant, not `:scope >`: Shopify's own section wrapper can sit
          between the slot and the old nav. The self-guard is what keeps this
          safe -- our own nav is a .t4s-navigation inside us, and this.contains()
          skips it.
        */
        slot.querySelectorAll('.t4s-navigation').forEach((node) => {
          if (node === this || this.contains(node)) return;
          node.setAttribute('data-menu-nav-replaced', '');
          node.style.display = 'none';
        });
      }

      /*
        The drawer cannot be displaced by hiding #menu-mb__ul. Every mb_nav block
        type renders as an <li> inside that one <ul> -- read at source in
        sections/mb_nav.liquid, where `help`, `cur`, `lang`, `wis`, `compe`,
        `sea` and `acc` each emit a level-0 <li> alongside the `menu` ones. Hiding
        the list took the utilities with it, which the agreed scope says stay. So
        hide the menu items only. The seven utility blocks are identifiable
        because the theme gives each a fixed id; a `menu` or `catpr` block gets
        `item_<block.id>` instead, so anything not on this list is menu content.
      */
      static UTILITY_IDS = [
        'item_mb_help', 'item_mb_cur', 'item_mb_lang',
        'item_mb_wis', 'item_mb_compe', 'item_mb_sea', 'item_mb_acc'
      ];

      displaceDrawer(slot) {
        slot.querySelectorAll('.t4s-mb__menu').forEach((list) => {
          if (list === this || this.contains(list)) return;
          list.querySelectorAll(':scope > li.t4s-item-level-0').forEach((item) => {
            if (MenuNav.UTILITY_IDS.indexOf(item.id) !== -1) return;
            item.setAttribute('data-menu-nav-replaced', '');
            item.style.display = 'none';
          });
        });
      }

      onClick(event) {
        if (!this.query.matches) return;
        const contact = event.target.closest('[data-menu-nav-contact]');
        if (contact && this.contains(contact)) {
          this.openContact(event, contact);
          return;
        }
        // Tabbed menu, drawer: a tab label drills into that tab instead of
        // following its link (the "View all" link inside still reaches it), and
        // the tab's own back bar returns to the tab list.
        const tabBack = event.target.closest('.menu-tab__back');
        if (tabBack && this.contains(tabBack)) {
          event.preventDefault();
          event.stopPropagation();
          const tabs = tabBack.closest('.menu-tabs');
          if (tabs) tabs.classList.remove('is-tab-open');
          return;
        }
        const tabLabel = event.target.closest('.menu-tab__label');
        if (tabLabel && this.contains(tabLabel)) {
          event.preventDefault();
          event.stopPropagation();
          const tab = tabLabel.closest('.menu-tab');
          const scroller = this.closest(MenuNav.SCROLLER);
          if (scroller && scroller.scrollTop) scroller.scrollTop = 0;
          this.selectTab(tab);
          tab.parentElement.classList.add('is-tab-open');
          return;
        }
        // The back button inside an open panel returns to the full list.
        const back = event.target.closest('.menu-nav__back');
        if (back && this.contains(back)) {
          event.preventDefault();
          event.stopPropagation();
          const item = back.closest('.t4s-menu-item');
          if (item) this.drill(item, false);
          return;
        }
        // In the drawer the whole row of an item with a panel is the control,
        // not just the chevron. Tapping the label used to follow the link to the
        // collection, which is not what anyone opening a drawer menu means; the
        // panel's own "View all" link still reaches it. Leaf items have no
        // toggle, so they fall through and navigate as normal.
        let toggle = event.target.closest('.menu-nav__toggle');
        if (!toggle) {
          const link = event.target.closest('a');
          const row = link && link.parentElement;
          if (row && row.matches('.t4s-menu-item') && row.firstElementChild === link) {
            toggle = row.querySelector(':scope > .menu-nav__toggle');
          }
          // The theme closes the drawer on a link tap; keep it open for this one.
          if (toggle) event.stopPropagation();
        }
        if (!toggle || !this.contains(toggle)) return;
        event.preventDefault();
        const item = toggle.closest('.t4s-menu-item');
        if (item) this.drill(item, !item.classList.contains('is--open'));
      }

      /*
        Drill in or out of one item's panel. In the drawer an open panel
        replaces the list rather than expanding under it (the CSS keys off
        `is--drilled` on this element), so only one item is ever open here.

        Ordered so the tap never forces a layout. At 6x CPU throttle the old
        version spent 55 of its 66ms in a forced reflow: it flipped the classes,
        then walked the ancestors reading getComputedStyle and scrollHeight to
        find the scroller, which made the browser lay the drawer out
        synchronously before the slide could start. Now the scroll reset
        happens BEFORE the classes change, while layout is still clean, and the
        focus move waits until the first animated frame has been painted.
      */
      drill(item, open) {
        const scroller = this.closest(MenuNav.SCROLLER);
        if (scroller && scroller.scrollTop) scroller.scrollTop = 0;
        this.querySelectorAll('.t4s-menu-item.is--open').forEach((other) => {
          if (other !== item) this.setOpen(other, false);
        });
        this.setOpen(item, open);
        this.classList.toggle('is--drilled', open);
        this.classList.toggle('is--returning', !open);
        const target = open
          ? item.querySelector('.menu-nav__back')
          : item.querySelector(':scope > a');
        if (target) {
          requestAnimationFrame(() => requestAnimationFrame(() => target.focus({ preventScroll: true })));
        }
        this.dispatchEvent(
          new CustomEvent('menu-nav:toggle', {
            bubbles: true,
            detail: { id: item.id, open },
          })
        );
      }

      /*
        Tabbed menu (blocks/menu-tabbed.liquid, menu-tab.liquid). Every tab's
        panel is already in the DOM; selecting a tab only moves `is-active`,
        which assets/menu.css turns into "this panel shows". On desktop a
        tab is selected by hovering its label (after a short pause, so a
        diagonal move from the list into the panel does not flick through the
        tabs on the way), by focusing it, or with the arrow keys; a click
        follows the label's link as normal. In the drawer a tap drills in
        instead (onClick).
      */
      selectTab(tab) {
        if (!tab) return;
        const tabs = tab.parentElement;
        tabs.querySelectorAll(':scope > .menu-tab').forEach((other) => {
          const on = other === tab;
          other.classList.toggle('is-active', on);
          const label = other.querySelector(':scope > .menu-tab__label');
          if (label) label.setAttribute('aria-expanded', String(on));
        });
      }

      onTabPointer(event) {
        if (this.query.matches) return;
        const label = event.target.closest('.menu-tab__label');
        clearTimeout(this.tabIntent);
        if (!label || event.type !== 'pointerover') return;
        this.tabIntent = setTimeout(() => this.selectTab(label.closest('.menu-tab')), MenuNav.TAB_INTENT);
      }

      onTabFocus(event) {
        if (this.query.matches) return;
        const label = event.target.closest('.menu-tab__label');
        if (label && this.contains(label)) this.selectTab(label.closest('.menu-tab'));
      }

      onTabKey(event) {
        if (this.query.matches) return;
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const label = event.target.closest('.menu-tab__label');
        if (!label) return;
        const labels = [...label.closest('.menu-tabs').querySelectorAll(':scope > .menu-tab > .menu-tab__label')];
        const i = labels.indexOf(label);
        const next = labels[(i + (event.key === 'ArrowDown' ? 1 : -1) + labels.length) % labels.length];
        event.preventDefault();
        next.focus();
      }

      // Theme editor: selecting a Tab block shows that tab.
      onBlockSelect(event) {
        const tab = event.target && event.target.closest && event.target.closest('.menu-tab');
        if (tab && this.contains(tab)) this.selectTab(tab);
        this.editorOpen(event.target);
      }

      onBlockDeselect(event) {
        const item = MenuNav.dropdownOf(event.target);
        if (item && item === this.pinned) this.unpin();
      }

      /*
        Theme editor, desktop (#454): selecting a Dropdown block, or a Column,
        link or banner inside one, opens that dropdown so edits show as they're
        made. It uses the class the theme's own hover opens it with, so the
        panel sits exactly where shoppers see it: Kalles positions every panel
        up front, and in the editor des_adm.min.js re-runs Header.init after the
        section reloads, after which the editor selects the block again.
        Kalles's hoverIntent adds and removes that class too, so the selected
        item is pinned: a class watcher hands the panel to any other item the
        pointer opens and gives it back when none is open, and the pointer
        leaving the selected item doesn't close it. Shoppers never fire these
        events, so nothing changes for them.
      */
      static OPEN = 'is-action__hover';

      static dropdownOf(node) {
        return node && node.closest ? node.closest('.t4s-nav__ul > .t4s-menu-item.has--children') : null;
      }

      editorOpen(node) {
        if (!this.isConnected || this.query.matches) return;
        const item = MenuNav.dropdownOf(node);
        if (!item || !this.contains(item)) return;
        this.unpin();
        this.querySelectorAll(`.t4s-nav__ul > .t4s-menu-item.${MenuNav.OPEN}`).forEach((other) => {
          if (other !== item) other.classList.remove(MenuNav.OPEN);
        });
        this.pinned = item;
        this.pinWatcher = new MutationObserver(() => this.keepPinned());
        this.querySelectorAll('.t4s-nav__ul > .t4s-menu-item.has--children').forEach((li) => {
          this.pinWatcher.observe(li, { attributes: true, attributeFilter: ['class'] });
        });
        this.keepPinned();
        this.measure();
      }

      // Open the pinned item unless the pointer has another one open. Only
      // writes when the state is wrong, so the watcher settles after one pass.
      keepPinned() {
        const item = this.pinned;
        if (!item) return;
        const other = [...this.querySelectorAll(`.t4s-nav__ul > .t4s-menu-item.${MenuNav.OPEN}`)].some((li) => li !== item);
        if (other === item.classList.contains(MenuNav.OPEN)) item.classList.toggle(MenuNav.OPEN, !other);
      }

      unpin() {
        if (this.pinWatcher) this.pinWatcher.disconnect();
        this.pinWatcher = null;
        if (this.pinned) this.pinned.classList.remove(MenuNav.OPEN);
        this.pinned = null;
      }

      // Long enough to ignore a pointer crossing a label on its way into the
      // panel, short enough that a deliberate hover feels immediate.
      static TAB_INTENT = 90;

      setOpen(item, open) {
        item.classList.toggle('is--open', open);
        if (!open) {
          const tabs = item.querySelector('.menu-tabs.is-tab-open');
          if (tabs) tabs.classList.remove('is-tab-open');
        }
        const toggle = item.querySelector(':scope > .menu-nav__toggle');
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
      }

      /*
        "Send us a message" in the help row (blocks/menu-help.liquid). Where the
        store runs Gorgias it opens the chat; everywhere else it opens the
        fleet's contact modal (snippets/track-contact-modal.liquid, rendered
        beside this element by sections/menu.liquid), so the question
        arrives labelled with where it was asked. Only when neither exists is
        the tap left alone for the link's href, the contact page. Gorgias is
        installed per store and outside the theme -- live on 10 of 25 stores on
        2026-09-25 -- and a script-tag install only arrives after window
        'load', so a tap before then gets the modal.

        What Gorgias dictates:
        - Before its bundle has run, window.GorgiasChat is a Proxy that THROWS
          on any property but init. So nothing is feature-tested except init,
          and every other call goes through the object init() resolves to.
          Never define a GorgiasChat stub here: the loader skips itself when
          one exists.
        - init() settles only once the bundle has loaded. On a store whose
          widget is switched off it may never settle, so a timer hands the tap
          to the modal rather than leaving it dead.
        - open() lands on the widget's own home screen, the one the chat
          bubble opens, whose first button is its own "Send us a message".
          Deliberately no setPage('conversation'), although Gorgias documents
          it as the way to reach the message form: on haverford.com.au, whose
          widget runs Gorgias's self-service portal, it routed to
          /offline-capture and drew a blank window (measured 2026-09-25 at
          375px). Each store configures its own widget, so the home screen
          is the only page certain to render on all of them.

        The drawer is closed first either way. Kalles keeps one drawer open at
        a time, and the drawer would otherwise stay open, aria-hidden=false,
        under the chat or the dialog.
      */
      openContact(event, link) {
        const modalTrigger = this.contactTrigger();
        const modal = modalTrigger ? modalTrigger.closest('[data-track-contact-modal]') : null;
        // The Gorgias-or-pop-up rule lives in snippets/track-enquiry (#531), which
        // every contact link and enquiry button shares. This help row passes its
        // own pop-up so its emails keep "Source: menu-help".
        if (window.TrackEnquiry) {
          this.closeDrawer();
          const took = window.TrackEnquiry.open({
            source: 'menu-help',
            modal: modal,
            href: link.href,
            onChat: () => this.dispatchEvent(new CustomEvent('menu-nav:contact', { bubbles: true, detail: { via: 'chat' } }))
          });
          if (took) {
            event.preventDefault();
            event.stopPropagation();
          }
          return;
        }
        const chat = window.GorgiasChat;
        const hasChat = !!chat && typeof chat.init === 'function';
        if (!hasChat && !modalTrigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer();
        // The snippet binds its own trigger and emits track:contact:open from
        // it, so the modal is opened through that button rather than by
        // reaching into the dialog.
        if (!hasChat) {
          modalTrigger.click();
          return;
        }
        let settled = false;
        const fallback = () => {
          if (settled) return;
          settled = true;
          if (modalTrigger) modalTrigger.click();
          else window.location.href = link.href;
        };
        const timer = setTimeout(fallback, MenuNav.CONTACT_TIMEOUT);
        chat.init().then(
          () => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            window.GorgiasChat.open();
            this.dispatchEvent(new CustomEvent('menu-nav:contact', { bubbles: true, detail: { via: 'chat' } }));
          },
          () => {
            clearTimeout(timer);
            fallback();
          }
        );
      }

      // Long enough for a slow phone to finish init(), short enough that a tap
      // on a store whose widget never starts does not look dead.
      static CONTACT_TIMEOUT = 4000;

      // The contact modal's own (hidden) trigger. It sits outside this element,
      // which moves into the drawer, so it is found through data-contact.
      contactTrigger() {
        const id = this.getAttribute('data-contact');
        const wrapper = id && document.getElementById(id);
        return wrapper ? wrapper.querySelector('[data-track-contact-open]') : null;
      }

      // Close through the overlay the theme already listens on, as
      // snippets/render_bottom.liquid and assets/search-overlay.js do. The
      // visible overlay is picked because another drawer may own the first.
      closeDrawer() {
        if (!document.body.classList.contains('is--opend-drawer')) return;
        const overlay = document.querySelector('.t4s-close-overlay.is--visible') ||
          document.querySelector('.t4s-close-overlay');
        if (overlay) overlay.click();
      }

      // Leaving the drawer width must not strand an item in its open state,
      // because `is--open` means something different to the desktop CSS.
      onChange() {
        this.place();
        if (this.query.matches) return;
        this.classList.remove('is--drilled', 'is--returning');
        this.querySelectorAll('.t4s-menu-item.is--open').forEach((item) => this.setOpen(item, false));
        this.querySelectorAll('.menu-tabs.is-tab-open').forEach((tabs) => tabs.classList.remove('is-tab-open'));
      }
    }
  );
}

/*
  Mobile menu drawer open state and the menu button's bars-to-cross (#224),
  moved here from snippets/render_bottom.liquid so it runs only with the
  menu-blocks header. See the drawer section at the end of assets/menu.css.
*/
(function () {
  const drawer = document.getElementById('t4s-menu-drawer');
  if (!drawer) return;

  /* The open class carries the drawer's geometry -- it starts below the header
     rather than at the top of the screen. Dropping it the moment closing
     begins snapped the panel up by the header's height while it was still
     sliding out, and flashed the dim overlay across the header. So it is held
     until the slide has finished: on the drawer's own transitionend, with a
     timeout in case the transition never fires (reduced motion, a tab in the
     background). Opening applies it immediately. */
  let closing = 0;
  const sync = () => {
    const open = drawer.getAttribute('aria-hidden') === 'false';
    /* The menu button's bars-to-cross animation follows the drawer's state
       immediately in both directions, unlike the geometry class below. */
    document.body.classList.toggle('is--menu-drawer-active', open);
    clearTimeout(closing);
    if (open) {
      document.body.classList.add('is--menu-drawer-open');
      return;
    }
    const done = () => {
      clearTimeout(closing);
      drawer.removeEventListener('transitionend', onEnd);
      document.body.classList.remove('is--menu-drawer-open');
    };
    const onEnd = (event) => { if (event.target === drawer && event.propertyName === 'transform') done(); };
    drawer.addEventListener('transitionend', onEnd);
    closing = setTimeout(done, 700);
  };
  new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  sync();

  /* The button is an <a href="/"> so it still works without JS. With JS the
     navigation is never wanted, so cancel it on every tap, open or closed --
     not only when we believe the drawer is open. Tapping the cross during the
     open animation used to fall through to the href and send the shopper to
     the homepage, which looks exactly like the cross doing nothing.

     Open state is read from the drawer itself rather than from the body class
     this script maintains, because that class arrives via MutationObserver
     and a fast second tap can beat it. Closing goes through the overlay the
     theme already listens on rather than a second implementation of close,
     and the visible overlay is picked specifically: a bare querySelector
     takes the first in the DOM, which may belong to another drawer. Capture
     phase, so the theme's own opener never runs on a tap meaning "close". */
  const isOpen = () => drawer.getAttribute('aria-hidden') === 'false' ||
    document.body.classList.contains('is--menu-drawer-open');

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-menu-drawer]')) return;
    event.preventDefault();
    if (!isOpen()) return;
    event.stopPropagation();
    const overlay = document.querySelector('.t4s-close-overlay.is--visible') ||
      document.querySelector('.t4s-close-overlay');
    overlay?.click();
    /* Kalles' close (T4SThemeSP.Drawer.close) acts only while its own open flag is
       set, and on a real phone that flag drifted out of step with the drawer after
       the old X button closed it: the overlay click was ignored and the cross took
       several taps (Template-Repository#751, Lee's on-phone log 2026-10-30). If the
       drawer is still open, re-sync the flag through Kalles' own API and close. */
    const kalles = window.T4SThemeSP && window.T4SThemeSP.Drawer;
    if (drawer.getAttribute('aria-hidden') === 'false' && kalles && window.jQuery_T4NT) {
      const $ = window.jQuery_T4NT;
      kalles.opend($(drawer));
      /* Name the menu drawer: with another drawer open too (the mini cart),
         an untargeted close throws. close(event, el) reads el's target. */
      kalles.close(undefined, $('<i data-drawer-target="#t4s-menu-drawer"></i>'));
    }
  }, true);
})();

/*
  Search triggers the menu-blocks header does not own -- the mobile toolbar's
  search icon (sections/toolbar_mobile.liquid) -- still point at Kalles's old
  search drawer (#t4s-search-hidden). With this header on the page they open the
  full-screen search overlay (#172) instead, the same one the header's own icon
  opens. Done here rather than in toolbar_mobile.liquid because Bravo Mobile
  Sports carries its own copy of that file, so it cannot be synced; this script
  loads only with the menu-blocks header, so Kalles headers keep their drawer.
*/
(function () {
  const OLD_TRIGGER = '[data-drawer-options*="t4s-search-hidden"]';
  const repoint = () => {
    document.querySelectorAll(OLD_TRIGGER).forEach((el) => {
      el.removeAttribute('data-drawer-options');
      el.removeAttribute('data-drawer-delay-');
      el.setAttribute('data-search-overlay-open', '');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repoint);
  else repoint();
})();
